// Docs.tsx (v4) — STEP-DOCS-PWA-READ-SURFACE
//
// Docs browsing tab — list, search, and open documents stored in Postgres.
// Fetches via fetchDocsList() / fetchDocsSearch(). Tapping a row opens the
// existing DocsSheet modal via openDocSheet(). Mobile-first layout (R-X05).

import { useEffect, useState } from "react";
import { fetchDocsList, fetchDocsSearch } from "../../api/client.js";
import type { DocsTreeItem } from "../../api/types.js";
import { openDocSheet } from "../DocsSheet.js";

// Canonical category names — normalizes overlapping doc path prefixes so that
// variant spellings (audit/audits, issues/known-issues, ops/ops-reports) are
// merged into one group header. VLR-1150 issue 10.
const PREFIX_CANON: Record<string, string> = {
  "docs/audits":      "docs/audit",
  "docs/known-issues": "docs/issues",
  "docs/ops-reports": "docs/ops",
};

function canonicalizePrefix(prefix: string): string {
  return PREFIX_CANON[prefix] ?? prefix;
}

// Group docs by their top-level path prefix (e.g. "docs/standards/").
function groupByPrefix(docs: DocsTreeItem[]): Map<string, DocsTreeItem[]> {
  const groups = new Map<string, DocsTreeItem[]>();
  for (const doc of docs) {
    const parts = doc.path.split("/");
    // Use first two path segments as group key (e.g. "docs/standards")
    const raw = parts.length > 1 ? parts.slice(0, 2).join("/") : parts[0];
    const prefix = canonicalizePrefix(raw);
    if (!groups.has(prefix)) groups.set(prefix, []);
    groups.get(prefix)!.push(doc);
  }
  // Sort groups by prefix name
  return new Map([...groups.entries()].sort(([a], [b]) => a.localeCompare(b)));
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  return `${(bytes / 1024).toFixed(1)}KB`;
}

export function DocsScreen() {
  const [docs, setDocs] = useState<DocsTreeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input 300ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Load on mount and when debouncedSearch changes.
  // Uses a cancelled flag to prevent stale responses from overwriting fresh
  // ones when rapid search input triggers multiple concurrent fetches.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetchDocs = async () => {
      try {
        const res = debouncedSearch.trim()
          ? await fetchDocsSearch(debouncedSearch.trim())
          : await fetchDocsList();
        if (!cancelled) setDocs(res.tree ?? []);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load documents");
          setDocs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchDocs();
    return () => { cancelled = true; };
  }, [debouncedSearch]);

  const grouped = groupByPrefix(docs);

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Search bar */}
      <div
        style={{
          padding: "14px 20px 10px",
          borderBottom: "1px solid var(--border-soft)",
        }}
      >
        <input
          type="text"
          placeholder="Search documents…"
          value={search}
          onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: "var(--bg-elev)",
            border: "1px solid var(--border-soft)",
            borderRadius: 8,
            padding: "10px 12px",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: "var(--text)",
            minHeight: 44,
          }}
        />
      </div>

      {/* Loading state */}
      {loading && (
        <div
          style={{
            padding: "48px 20px",
            textAlign: "center",
            color: "var(--text-dim)",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
          }}
        >
          Loading documents…
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div
          style={{
            margin: "20px",
            padding: "16px",
            background: "var(--danger-soft)",
            border: "1px solid var(--danger)",
            borderRadius: 8,
            color: "var(--danger)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Failed to load documents</div>
          <div>{error}</div>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && docs.length === 0 && (
        <div
          style={{
            padding: "48px 20px",
            textAlign: "center",
            color: "var(--text-dim)",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
          }}
        >
          No documents found
        </div>
      )}

      {/* Doc list grouped by prefix */}
      {!loading && !error && docs.length > 0 && (
        <div>
          {[...grouped.entries()].map(([prefix, items]) => (
            <div key={prefix}>
              {/* Group header */}
              <div
                style={{
                  padding: "10px 20px 4px",
                  fontFamily: "var(--font-mono)",
                  fontSize: 9,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "var(--text-dim)",
                  borderBottom: "1px solid var(--border-soft)",
                  background: "var(--bg-bar)",
                }}
              >
                {prefix}/
              </div>

              {/* Doc rows */}
              {items.map((doc) => (
                <button
                  key={doc.path}
                  onClick={() => openDocSheet(doc.path, doc.title)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 20px",
                    background: "transparent",
                    border: "none",
                    borderBottom: "1px solid var(--border-soft)",
                    cursor: "pointer",
                    minHeight: 44,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        lineHeight: 1.35,
                        color: "var(--text)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {doc.title}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 9,
                        color: "var(--text-dim)",
                        marginTop: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {doc.path}
                    </div>
                  </div>
                  <span
                    className="chip"
                    style={{
                      fontSize: 9,
                      flexShrink: 0,
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {formatSize(doc.size)}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
